import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { getApiUrl } from '../utils/api';
import { getGlobalUserRoleBadgeInfo } from '../utils/roleBadges';
import './MyProfile.css';

// Dados pessoais que qualquer usuário — sem exceção de papel — pode ver e
// corrigir sobre si mesmo (nome, foto, nascimento, e-mail do Jira). Papel,
// empresa e status continuam exclusivos de quem gerencia usuários (tela
// Usuários e Funções): aqui é só autoatendimento do próprio titular dos
// dados.
function formatDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

const MyProfile = () => {
  const { user, setUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const fileInputRef = useRef(null);

  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [birthDate, setBirthDate] = useState(formatDateInput(user?.birth_date));
  const [jiraEmail, setJiraEmail] = useState(user?.jira_email || '');
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [removingAvatar, setRemovingAvatar] = useState(false);

  const dirty = (
    firstName !== (user?.first_name || '') ||
    lastName !== (user?.last_name || '') ||
    birthDate !== formatDateInput(user?.birth_date) ||
    jiraEmail !== (user?.jira_email || '')
  );

  const roleBadge = getGlobalUserRoleBadgeInfo ? getGlobalUserRoleBadgeInfo(user?.role) : null;
  const initials = `${(user?.first_name || '?')[0] || ''}${(user?.last_name || '')[0] || ''}`.toUpperCase();
  // Conta TRIAL/UNITÁRIO é individual — não tem ninguém "na empresa" pra
  // pedir mudança de papel/status, então a explicação precisa ser outra.
  const isSoloAccount = ['TRIAL', 'UNITARIO'].includes(String(user?.role || '').toUpperCase());

  async function handleSave() {
    if (!firstName.trim() || !lastName.trim()) {
      showError('Nome e sobrenome não podem ficar vazios');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('auth/me'), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          birth_date: birthDate || null,
          jira_email: jiraEmail.trim() || null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || 'Erro ao salvar dados pessoais');
        return;
      }
      setUser(prev => ({
        ...prev,
        first_name: data.user.first_name,
        last_name: data.user.last_name,
        birth_date: data.user.birth_date,
        jira_email: data.user.jira_email,
        name: `${data.user.first_name} ${data.user.last_name}`.trim()
      }));
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, ...data.user, name: `${data.user.first_name} ${data.user.last_name}`.trim() }));
      showSuccess('Dados pessoais atualizados com sucesso');
    } catch (err) {
      console.error('Erro ao salvar perfil:', err);
      showError('Erro de conexão ao salvar');
    } finally {
      setSaving(false);
    }
  }

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showError('Selecione um arquivo de imagem');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError('Imagem muito grande (máximo 5MB)');
      return;
    }
    setUploadingAvatar(true);
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('avatar', file);
      const res = await fetch(getApiUrl('auth/me/avatar'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || 'Erro ao enviar foto');
        return;
      }
      setUser(prev => ({ ...prev, avatar_url: data.avatar_url }));
      showSuccess('Foto de perfil atualizada');
    } catch (err) {
      console.error('Erro ao enviar avatar:', err);
      showError('Erro de conexão ao enviar foto');
    } finally {
      setUploadingAvatar(false);
    }
  }

  async function handleRemoveAvatar() {
    setRemovingAvatar(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('auth/me/avatar'), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || 'Erro ao remover foto');
        return;
      }
      setUser(prev => ({ ...prev, avatar_url: null }));
      showSuccess('Foto de perfil removida');
    } catch (err) {
      console.error('Erro ao remover avatar:', err);
      showError('Erro de conexão ao remover foto');
    } finally {
      setRemovingAvatar(false);
    }
  }

  return (
    <div className="myprofile-page">
      <div className="myprofile-header">
        <h1>👤 Meu Perfil</h1>
        <p>Seus dados pessoais — visíveis e editáveis só por você.</p>
      </div>

      <div className="myprofile-card">
        <div className="myprofile-avatar-block">
          <div className="myprofile-avatar">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="Foto de perfil" />
            ) : (
              <span>{initials || '👤'}</span>
            )}
            {(uploadingAvatar) && <div className="myprofile-avatar-overlay">Enviando...</div>}
          </div>
          <div className="myprofile-avatar-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAvatar || removingAvatar}
            >
              {user?.avatar_url ? 'Trocar foto' : 'Adicionar foto'}
            </button>
            {user?.avatar_url && (
              <button
                type="button"
                className="btn btn-link-danger"
                onClick={handleRemoveAvatar}
                disabled={uploadingAvatar || removingAvatar}
              >
                Remover
              </button>
            )}
          </div>
        </div>

        <div className="myprofile-identity">
          <div className="myprofile-name">{user?.first_name} {user?.last_name}</div>
          <div className="myprofile-identity-badges">
            {roleBadge && (
              <span className={`myprofile-role-badge ${roleBadge.class}`}>
                {roleBadge.text}
              </span>
            )}
            {user?.company_name && <span className="myprofile-company">{user.company_name}</span>}
          </div>
        </div>
      </div>

      <div className="myprofile-card">
        <h3>Dados pessoais</h3>
        <div className="myprofile-form-grid">
          <div className="myprofile-field">
            <label>Nome</label>
            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={80} />
          </div>
          <div className="myprofile-field">
            <label>Sobrenome</label>
            <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={80} />
          </div>
          <div className="myprofile-field">
            <label>Data de nascimento</label>
            <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
          </div>
          <div className="myprofile-field">
            <label>E-mail do Jira</label>
            <input type="email" value={jiraEmail} onChange={(e) => setJiraEmail(e.target.value)} placeholder="seu-email@empresa.com" />
          </div>
          <div className="myprofile-field myprofile-field-readonly">
            <label>E-mail de acesso</label>
            <input type="email" value={user?.email || ''} disabled title="O e-mail de acesso não pode ser alterado por aqui" />
          </div>
        </div>
        <p className="myprofile-note">
          {isSoloAccount
            ? 'Papel e status da conta fazem parte do seu plano — fale com o suporte pra alterar.'
            : 'Papel e status da conta são gerenciados por quem cuida dos usuários na sua empresa — fale com essa pessoa pra alterar.'}
        </p>
        <div className="myprofile-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
